import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Tracks the last "trusted" page (library or category) the user visited in
 * the current tab so the post Preview's back button can return there —
 * regardless of how many related-article hops happened in between.
 *
 * Trusted patterns:
 *   /group/:slug/library            → Biblioteca
 *   /group/:slug/p/c/:catId         → Categoría
 *
 * Other paths (like the post itself, /group/:slug/p/:id) are intentionally
 * NOT tracked, so the previous trusted entry survives a chain like
 * library → A → B → C — back from any of A/B/C still goes to library.
 *
 * sessionStorage scope: per-tab, cleared when the tab closes — exactly the
 * lifetime we want for "where did this browsing session start".
 */

const STORAGE_KEY = "klouser:lastTrustedReferrer";

const LIBRARY_RE = /^\/group\/[^/]+\/library/;
const CATEGORY_RE = /^\/group\/[^/]+\/p\/c\//;
// Matches dashboard post-list pages:
//   /dashboard/membership/:id/posts
//   /dashboard/experience/:id/posts
//   /dashboard/community/events/:id/posts
//   /dashboard/community/:id/lead-magnets
const DASHBOARD_POSTS_RE = /^\/dashboard\/.*\/(posts|lead-magnets)\/?$/;

const isTrusted = (pathname: string): boolean =>
  LIBRARY_RE.test(pathname) ||
  CATEGORY_RE.test(pathname) ||
  DASHBOARD_POSTS_RE.test(pathname);

export type TrustedReferrerKind = "library" | "category" | "dashboard-posts";

export interface TrustedReferrer {
  url: string;
  kind: TrustedReferrerKind;
}

const classify = (pathname: string): TrustedReferrerKind | null => {
  if (LIBRARY_RE.test(pathname)) return "library";
  if (CATEGORY_RE.test(pathname)) return "category";
  if (DASHBOARD_POSTS_RE.test(pathname)) return "dashboard-posts";
  return null;
};

export const useTrustedReferrerTracker = (): void => {
  const location = useLocation();
  useEffect(() => {
    if (isTrusted(location.pathname)) {
      sessionStorage.setItem(
        STORAGE_KEY,
        location.pathname + location.search + location.hash
      );
    }
  }, [location.pathname, location.search, location.hash]);
};

export const getTrustedReferrer = (): TrustedReferrer | null => {
  if (typeof window === "undefined") return null;
  const url = sessionStorage.getItem(STORAGE_KEY);
  if (!url) return null;
  const pathname = url.split("?")[0].split("#")[0];
  const kind = classify(pathname);
  if (!kind) return null;
  return { url, kind };
};
